import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import BuyForStorage from '../../BuyForStorage/BuyForStorage';
import BuyForDelivery from '../../BuyForDelivery/BuyForDelivery';
import {
  emptyProductTiers,
  fetchProductTiers,
} from '../../../store/actions/products/productActions';
import { getCurrentAccount } from '../../../store/selectors/accountSelectors';
import {
  LOCATION_TYPE_DELIVERY,
  productLocationTypes,
  productLocationTypesByName,
} from '../../../util/enum/api/productTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { selectProductTiers } from '../../../store/selectors/productsSelector';
import useGetPeriodicPriceTiers from '../../../util/hooks/useGetPeriodicPriceTiers';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { COUNTDOWN_TIMER_INTERVAL_MS } from '../../../constants/timerConstants';

const BuyOrder = ({
  setWizardData,
  wizardData,
  handleBack,
  handleNext,
  handleClose,
  setWizardTitle,
  openSupport,
  isSupportOpen,
  skipChooseLocation,
  unverifiedBankAccounts,
  isLockedPrice,
  counter,
  unity,
}) => {
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const productTiers = useSelector(selectProductTiers);
  const account = useSelector(getCurrentAccount);
  const { selectedVault } = wizardData;
  const { SymbolCode, LocationType } = selectedVault;

  const [isLockedPriceUser, setIsLockedPriceUser] = useState(false);
  const [counterValue, setCounterValue] = useState(
    Math.floor(
      settings.PricingRefreshFrequencyMilliseconds /
        COUNTDOWN_TIMER_INTERVAL_MS,
    ),
  );
  const [unityValue, setUnityValue] = useState(false);

  const periodicCallback = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounterValue((prevCounter) => prevCounter - prevCounter + counter);
      setUnityValue(unity);
    }
  }, []);

  useEffect(() => {
    if (!settings || isEmpty(settings) || !settings.BrokerCode) {
      return;
    }

    setIsLockedPriceUser(settings?.IsLockedPricingActive);
  }, [settings]); //eslint-disable-line

  useEffect(() => {
    if (!selectedVault || !isLockedPrice) {
      return;
    }

    dispatch(
      fetchProductTiers({
        accountUid: account.AccountUid,
        symbol: SymbolCode,
        side: 'Buy',
        location:
          LocationType === LOCATION_TYPE_DELIVERY
            ? Object.keys(productLocationTypesByName).find(
                (key) => productLocationTypesByName[key] === 12,
              )
            : parseEnumType(productLocationTypes, LocationType),
        trueTiers: true,
      }),
    );

    return () => dispatch(emptyProductTiers());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVault]);

  useGetPeriodicPriceTiers(
    'priceTiersBO',
    account,
    LocationType === LOCATION_TYPE_DELIVERY
      ? Object.keys(productLocationTypesByName).find(
          (key) => productLocationTypesByName[key] === 12,
        )
      : parseEnumType(productLocationTypes, LocationType),
    SymbolCode,
    'Buy',
    dispatch,
    isLockedPriceUser,
    settings?.PricingRefreshFrequencyMilliseconds,
    true,
    periodicCallback,
  );

  if (isEmpty(wizardData)) {
    return <div />;
  }

  if (!selectedVault) {
    return <div />;
  }

  if (LocationType === 12) {
    return (
      <BuyForDelivery
        closeModal={handleClose}
        setWizardData={setWizardData}
        wizardData={wizardData}
        handleBack={handleBack}
        handleNext={handleNext}
        setWizardTitle={setWizardTitle}
        openSupport={openSupport}
        isSupportOpen={isSupportOpen}
        skipChooseLocation={skipChooseLocation}
        unverifiedBankAccounts={unverifiedBankAccounts}
        tiers={productTiers?.Tiers}
        counter={isLockedPrice ? counterValue : counter}
        unity={isLockedPrice ? unityValue : unity}
      />
    );
  }

  return (
    <BuyForStorage
      closeModal={handleClose}
      setWizardData={setWizardData}
      wizardData={wizardData}
      handleBack={handleBack}
      handleNext={handleNext}
      setWizardTitle={setWizardTitle}
      skipChooseLocation={skipChooseLocation}
      tiers={productTiers?.Tiers}
      counter={isLockedPrice ? counterValue : counter}
      unity={isLockedPrice ? unityValue : unity}
    />
  );
};

BuyOrder.propTypes = {
  wizardData: PropTypes.shape({
    selectedVault: PropTypes.shape({
      LocationType: PropTypes.number,
      SymbolCode: PropTypes.string,
    }),
    storageProduct: PropTypes.shape({
      Locations: PropTypes.arrayOf(PropTypes.shape({})),
      SymbolCode: PropTypes.string,
    }),
  }),
  handleNext: PropTypes.func,
  handleBack: PropTypes.func,
  handleClose: PropTypes.func,
  setWizardData: PropTypes.func,
  setWizardTitle: PropTypes.func,
  openSupport: PropTypes.func,
  isSupportOpen: PropTypes.bool,
  skipChooseLocation: PropTypes.bool,
  unverifiedBankAccounts: PropTypes.arrayOf(
    PropTypes.shape({
      BankAccountUid: PropTypes.string,
      BankName: PropTypes.string,
      AccountMask: PropTypes.string,
      IsVerified: PropTypes.bool,
    }),
  ),
  isLockedPrice: PropTypes.bool,
  counter: PropTypes.number,
  unity: PropTypes.bool,
};

export default BuyOrder;
